import React, { memo, useMemo, useState, useEffect, useCallback } from "react"
import { useLocalization } from "../../../../hooks/localization"
import { useRPC } from "../../../../hooks/rpc"
import { useHistory, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { PasswordContainer } from "../../../../components/PasswordContainer"
import { SettingsPageContainer } from "../../../../components/Settings/SettingsPageContainer"
import { SettingCheckBoxComponent } from '../../../../components/Settings/SettingCheckboxComponent';
import { BridgeDeviceModel } from "../../../../types/bridge-device";
import { useMessage } from '../../../../hooks/message';

const BACK_URL = '/settings/keypad/';


export const KeypadEditScreen = memo(() => {
    const { t } = useLocalization();
    const history = useHistory();
    const [rpc] = useRPC();
    const { success, failure } = useMessage();

    const bridgeDeviceList = useSelector(
        (state: { control: { bridgeDeviceList: BridgeDeviceModel[] } }
        ) => state.control.bridgeDeviceList)
    const params = useParams<{ device_id: string; input_id: string }>()
    const device = useMemo(() => bridgeDeviceList.find(
        (device: BridgeDeviceModel) => device.id === params.device_id) || null,
        [bridgeDeviceList, params.device_id]
    )

    const [feedbackState, setFeedbackState] = useState(false);
    const [buzzerState, setBuzzerState] = useState(false);
    const [isActiveState, setIsActive] = useState(false);
    const [isActiveStateLocked, setIsActiveLocked] = useState(false);

    const toggleTouchFeedback = () => {
        setFeedbackState((state) => !state)
    }
    const toggleBuzzerStatus = () => {
        setBuzzerState((state) => !state)
    }
    const toggleIsActive = () => {
        setIsActive((state) => !state)
    }

    useEffect(() => {
        const keypadConfig = device.keypad_config;
        const isActive = !(keypadConfig.is_passive || false);
        setIsActiveLocked(isActive);
        setIsActive(isActive);
        setBuzzerState(keypadConfig.buzzer_status);
        setFeedbackState(keypadConfig.touch_feedback);
    }, [device]);


    const backButtonCallback = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const saveButtonCallback = useCallback(() => {
        const keypad_config = {
            "bridge_device_id": device.id,
            "touch_feedback": feedbackState,
            "buzzer_status": buzzerState,
            "is_passive": !isActiveState
        }
        rpc.updateKeypadSettings(keypad_config).then(() => {
            success(t("keypad_settings_successfully_edited"))
            backButtonCallback();
        }).catch(e => {
            if (e !== 'cant_read') {
                failure(e);
            }
        });
    }, [device, feedbackState, buzzerState, isActiveState, rpc, backButtonCallback, t, success, failure]);


    const buttons = useMemo(() => [
        {
            text: t('save'),
            className: 'green',
            onClick: saveButtonCallback,
            doNotBlockUI: true,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, saveButtonCallback]);

    return (
        <PasswordContainer requiredRole="user" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
            >
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingCheckBoxComponent
                            label={t('touch_feedback')}
                            set={toggleTouchFeedback}
                            value={feedbackState}
                        />
                        <SettingCheckBoxComponent
                            label={t('buzzer_status')}
                            set={toggleBuzzerStatus}
                            value={buzzerState}
                        />
                        <SettingCheckBoxComponent
                            label={t('is_active')}
                            set={toggleIsActive}
                            value={isActiveState}
                            disabled={isActiveStateLocked}
                        />
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})